import * as i0 from '@angular/core';
import { Injectable, EventEmitter, TemplateRef, Component, Inject, Input, ViewChild, Output, Optional, Attribute, HostListener, Directive, Self, NgModule } from '@angular/core';
import { Subject, fromEvent, filter, take } from 'rxjs';
import { TemplatePortal, PortalModule } from '@angular/cdk/portal';
import * as i3 from '@angular/common';
import { DOCUMENT, CommonModule } from '@angular/common';
import * as i1 from '@angular/cdk/overlay';
import { OverlayModule } from '@angular/cdk/overlay';

class MenuService {
    constructor() {
        this.menuItems = [];
        this.clickedMenuState = new Subject();
        this.clickedMenuState$ = this.clickedMenuState.asObservable();
    }
    /**
     * It returns the first menuItem in the menuItems array that has an id property that matches the id
     * parameter
     * @param {string} id - The id of the menu item to find.
     * @returns The first menuItem in the menuItems array that has an id that matches the id passed in as
     * an argument.
     */
    findComponent(id) {
        return this.menuItems.find((item) => item.id === id);
    }
    setClickedMenuState(state, menu) {
        this.clickedMenuState.next({ state, menu });
    }
}
MenuService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.7", ngImport: i0, type: MenuService, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
MenuService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "15.2.7", ngImport: i0, type: MenuService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.7", ngImport: i0, type: MenuService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }] });

/**
 * The MenuComponent create menu container.
 *
 * MenuComponent component accepts [id] property which is the content to be displayed in the popover menu.
 *
 * ```ts
 * @Input() id: string - Menu ID with associated forMenu or forContextMenu.
 * ```
 *
 * <sub>app.component.html</sub>
 *
 * Add the below code to your code stack and give initializer parameters.
 *
 * ```html
 * <button forContextMenu="menu1" class="btn btn-primary me-2">Right Click Here</button>
 * <button forMenu="menu2" class="btn btn-primary">Click Here</button>
 *
 * <tbtk-menu id="menu1">
 *   <tbtk-menu-item icon="save.svg" (click)="test('save')" forMenu="menu2">Save</tbtk-menu-item>
 *   <tbtk-menu-item icon="remove.svg" (click)="test('remove')">Remove</tbtk-menu-item>
 *   <tbtk-menu-item icon="share.svg" (click)="test('share')">Share</tbtk-menu-item>
 * </tbtk-menu>
 *
 * <tbtk-menu id="menu2">
 *   <tbtk-menu-item icon="close.svg" (click)="test('close')">Close</tbtk-menu-item>
 * </tbtk-menu>
 * ```
 */
class MenuComponent {
    set portal(template) {
        template && (this._templates = template);
    }
    constructor(overlay, document, menuService, viewContainerRef, cd) {
        this.overlay = overlay;
        this.document = document;
        this.menuService = menuService;
        this.viewContainerRef = viewContainerRef;
        this.cd = cd;
        this.arrow = false;
        this.data = {};
        this.listener$ = new EventEmitter();
        this.showing = false;
        this.active$ = new EventEmitter(false);
        let showing = false;
        // this.createPortalHostElement();
        this.listener$.subscribe(({ state, event, position }) => {
            this.showing = state;
            if (!showing) {
                showing = !showing;
                this.active$.emit(true);
                this.cd.detectChanges();
                this.attach(event, position);
            }
            else {
                showing = false;
                this.active$.emit(false);
                cd.detectChanges();
                this.close();
            }
        });
    }
    ngOnInit() {
        this.menuService.menuItems.push({ id: this.id, component: this });
    }
    onClick(event) {
        event;
        // Bunu comment altına altım çünkü bu aslında birden çok submenu açıldığında submenu itemlarından biri tıklandığında tüm parentları kapatmak için yapılmıştı.
        // Ama daha sonra bunu menu-item.component.ts içerisindeki @HostListener('click', ['$event']) ile yapmak daha mantıklı geldi ve oraya taşıdım.
        // Burada ilerde aksilik olursa bulunması faydalı, tekrar bu hale dönüştürebilirim. Alttaki comment satırını açarsan o şekle dönü
        this.listener$.emit({ state: false, event });
    }
    createPortalHostElement() {
        this.portalHostElement = this.document.querySelector('#portal-host');
        if (!this.portalHostElement) {
            this.portalHostElement = document.createElement('div');
            this.portalHostElement.setAttribute('id', 'portal-host');
            this.portalHostElement.classList.add('portal-host');
        }
        this.document.body.appendChild(this.portalHostElement);
    }
    attach(event, position) {
        const { originX, originY, overlayX } = position === 'bottom' ? { originY: 'bottom', originX: 'end', overlayX: 'end' } : { originY: 'top', originX: 'end', overlayX: 'start' };
        const positionStrategy = this.overlay
            .position()
            .flexibleConnectedTo(position === 'mouse' ? { x: event.x, y: event.y } : event.target)
            .withPositions([
            {
                originX,
                originY,
                overlayX,
                overlayY: 'top',
            },
            {
                originX: 'start',
                originY: 'top',
                overlayX: 'end',
                overlayY: 'top',
            },
        ]);
        this.overlayRef = this.overlay.create({
            backdropClass: 'popover-backdrop',
            positionStrategy,
            scrollStrategy: this.overlay.scrollStrategies.block(),
        });
        this.overlayRef.attach(new TemplatePortal(this._templates, this.viewContainerRef, {
            $implicit: { data: this.data },
        }));
        this.subscription = fromEvent(document, 'click')
            .pipe(filter((event) => {
            const clickTarget = event.target;
            return !!this.overlayRef && !this.overlayRef.overlayElement.contains(clickTarget);
        }), take(1))
            .subscribe(() => {
            this.listener$.emit({ state: false, event });
        });
    }
    close() {
        this.showing = false;
        this.overlayRef?.detach();
        this.subscription && this.subscription.unsubscribe();
        if (this.overlayRef) {
            this.overlayRef.dispose();
            this.overlayRef = null;
        }
    }
}
MenuComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.7", ngImport: i0, type: MenuComponent, deps: [{ token: i1.Overlay }, { token: DOCUMENT }, { token: MenuService }, { token: i0.ViewContainerRef }, { token: i0.ChangeDetectorRef }], target: i0.ɵɵFactoryTarget.Component });
MenuComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "15.2.7", type: MenuComponent, selector: "tbtk-menu", inputs: { id: "id", arrow: "arrow", data: "data", katmer: "katmer" }, outputs: { active$: "active$" }, viewQueries: [{ propertyName: "portal", first: true, predicate: ["tempRef"], descendants: true, read: TemplateRef }], ngImport: i0, template: "<ng-container *ngIf=\"showing\">\n  <ng-template #tempRef let-data>\n    <div class=\"tbtk-menu\" [ngClass]=\"{ arrow: arrow }\" (click)=\"onClick($event)\"><ng-content></ng-content></div>\n  </ng-template>\n</ng-container>\n", styles: [":host{display:inline-block;border:1px solid var(--sapphire-neutral-01);border-radius:.1875rem;box-shadow:.625rem 0 1.25rem #0000001a}.tbtk-menu{--menu-border-color: var(--neutral-02);--menu-border-radius: .25rem;--menu-background: #ffffff;z-index:1000;display:block;padding:.5em 0;border:1px solid var(--menu-border-color);border-radius:var(--menu-border-radius);box-shadow:0 0 12pt #00000040;background-color:var(--menu-background)}.tbtk-menu.arrow{position:relative;margin-top:.625rem}.tbtk-menu.arrow:before{content:\"\";position:absolute;top:-1rem;right:.5625rem;display:inline-block;border:.5rem solid transparent;border-bottom-color:var(--menu-border-color)}.tbtk-menu.arrow:after{content:\"\";position:absolute;top:-.83rem;right:.625rem;z-index:11111;display:inline-block;border:.4375rem solid transparent;border-bottom-color:#fff}.tbtk-menu ::ng-deep hr{margin-top:.25rem;margin-bottom:.25rem;border:none;border-bottom:1px solid var(--menu-border-color)}\n"], dependencies: [{ kind: "directive", type: i3.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "directive", type: i3.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.7", ngImport: i0, type: MenuComponent, decorators: [{
            type: Component,
            args: [{ selector: 'tbtk-menu', template: "<ng-container *ngIf=\"showing\">\n  <ng-template #tempRef let-data>\n    <div class=\"tbtk-menu\" [ngClass]=\"{ arrow: arrow }\" (click)=\"onClick($event)\"><ng-content></ng-content></div>\n  </ng-template>\n</ng-container>\n", styles: [":host{display:inline-block;border:1px solid var(--sapphire-neutral-01);border-radius:.1875rem;box-shadow:.625rem 0 1.25rem #0000001a}.tbtk-menu{--menu-border-color: var(--neutral-02);--menu-border-radius: .25rem;--menu-background: #ffffff;z-index:1000;display:block;padding:.5em 0;border:1px solid var(--menu-border-color);border-radius:var(--menu-border-radius);box-shadow:0 0 12pt #00000040;background-color:var(--menu-background)}.tbtk-menu.arrow{position:relative;margin-top:.625rem}.tbtk-menu.arrow:before{content:\"\";position:absolute;top:-1rem;right:.5625rem;display:inline-block;border:.5rem solid transparent;border-bottom-color:var(--menu-border-color)}.tbtk-menu.arrow:after{content:\"\";position:absolute;top:-.83rem;right:.625rem;z-index:11111;display:inline-block;border:.4375rem solid transparent;border-bottom-color:#fff}.tbtk-menu ::ng-deep hr{margin-top:.25rem;margin-bottom:.25rem;border:none;border-bottom:1px solid var(--menu-border-color)}\n"] }]
        }], ctorParameters: function () { return [{ type: i1.Overlay }, { type: Document, decorators: [{
                    type: Inject,
                    args: [DOCUMENT]
                }] }, { type: MenuService }, { type: i0.ViewContainerRef }, { type: i0.ChangeDetectorRef }]; }, propDecorators: { id: [{
                type: Input
            }], arrow: [{
                type: Input
            }], data: [{
                type: Input
            }], katmer: [{
                type: Input
            }], portal: [{
                type: ViewChild,
                args: ['tempRef', { read: TemplateRef }]
            }], active$: [{
                type: Output
            }] } });

/**
 * The `MenuItemComponent` creates a menu item inside a menu. It accepts several input properties to customize the content and appearance of the menu item.
 *
 * @description
 * The `MenuItemComponent` has the following input properties:
 * - `@Input() label` (type: `string`): The text that will be displayed in the menu item.
 * - `@Input() icon` (type: `string`): A URL or data URI for an image to be displayed as an icon in the menu item. If the `icon` input property is not set, the menu item will not display an icon.
 * - `@Input() iconClass` (type: `string`): A string that specifies one or more CSS classes to be applied to the menu item's icon. If the `class` input property is not set, the menu item icon class will not have any CSS classes applied to it.
 * - `@Input() forMenu` (type: `string`): A string that specifies the ID of the menu that the menu item belongs to. If the `forMenu` input property is set, the menu item will be displayed in the specified menu. If it is not set, the menu item will not belong to any menu.
 *
 * The `MenuItemComponent` also has two event listeners:
 * - `onMouseEnter`: This listener is triggered when the mouse enters the menu item element. If the `forMenu` input property is not set, the listener will emit an event to the parent `MenuComponent`.
 * - `onClick`: This listener is triggered when the menu item is clicked. If the `forMenu` input property is not set, the listener will execute a callback function specified in the parent `MenuComponent`.
 *
 * @example
 * <sub>app.component.html</sub>
 * Add the below code to your code stack and give initializer parameters.
 *
 * ```html
 * <tbtk-menu-item label="save" icon="svg" icon-class="save-icon" (click)="test('deneme 2')" forMenu="tempateId">Content Here</tbtk-menu-item>
 * ```
 */
class MenuItemComponent {
    constructor(parent, classNames) {
        this.parent = parent;
        this.classNames = classNames;
        this.label = '';
        this.icon = '';
        // eslint-disable-next-line @angular-eslint/no-input-rename
        this.iconClass = '';
        this.forMenu = '';
    }
    onMouseEnter(event) {
        if (!this.forMenu) {
            this.parent?.child?.listener$.emit({ state: false, event });
        }
    }
    onClick(event) {
        if (!this.forMenu) {
            // @TODO - Add the code to be executed when the click event is triggered.
            // this.parent?.listener$.emit({ state: false, event });
        }
    }
}
MenuItemComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.7", ngImport: i0, type: MenuItemComponent, deps: [{ token: MenuComponent, optional: true }, { token: 'class', attribute: true }], target: i0.ɵɵFactoryTarget.Component });
MenuItemComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "15.2.7", type: MenuItemComponent, selector: "tbtk-menu-item", inputs: { label: "label", icon: "icon", iconClass: ["icon-class", "iconClass"], forMenu: "forMenu" }, host: { listeners: { "mouseenter": "onMouseEnter($event)", "click": "onClick($event)" } }, ngImport: i0, template: "<span class=\"menu-item-icon\" [ngClass]=\"iconClass\"><img *ngIf=\"icon\" src=\"{{ icon }}\" alt=\"\" /></span>\n<span class=\"menu-item-label\" *ngIf=\"label\">{{ label }}</span>\n<span class=\"menu-item-content\"><ng-content></ng-content></span>\n", styles: [":host{--popover-menu-item-padding: 0 1.25rem 0 .625rem;--popover-menu-height: 2.125rem;--popover-menu-min-width: 9.75rem;--popover-text-color: var(--neutral-06);--popover-text-font-size: .875rem;--popover-text-danger-color: var(--danger-05);--popover-icon-margin-right: .625rem;--popover-hover-bg: var(--neutral-00);--popover-active-bg: var(--primary-01);--popover-focus-bg: var(--neutral-00);display:flex;flex:1;flex-direction:row;align-items:center;padding:var(--popover-menu-item-padding);min-width:var(--popover-menu-min-width);height:var(--popover-menu-height);border:none;background-color:transparent;color:var(--popover-text-color);font-size:var(--popover-text-font-size);font-weight:500;letter-spacing:-.017em;line-height:1.125rem;cursor:pointer;-webkit-user-select:none;user-select:none;gap:var(--popover-icon-margin-right)}:host:hover{background-color:var(--popover-hover-bg)}:host:active{background-color:var(--popover-active-bg)}:host:focus-visible{background-color:var(--popover-focus-bg)}:host .menu-item-icon{display:flex;align-items:center;justify-content:center;width:1rem;height:1rem}:host .menu-item-icon img{width:100%;height:100%}\n"], dependencies: [{ kind: "directive", type: i3.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "directive", type: i3.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.7", ngImport: i0, type: MenuItemComponent, decorators: [{
            type: Component,
            args: [{ selector: 'tbtk-menu-item', template: "<span class=\"menu-item-icon\" [ngClass]=\"iconClass\"><img *ngIf=\"icon\" src=\"{{ icon }}\" alt=\"\" /></span>\n<span class=\"menu-item-label\" *ngIf=\"label\">{{ label }}</span>\n<span class=\"menu-item-content\"><ng-content></ng-content></span>\n", styles: [":host{--popover-menu-item-padding: 0 1.25rem 0 .625rem;--popover-menu-height: 2.125rem;--popover-menu-min-width: 9.75rem;--popover-text-color: var(--neutral-06);--popover-text-font-size: .875rem;--popover-text-danger-color: var(--danger-05);--popover-icon-margin-right: .625rem;--popover-hover-bg: var(--neutral-00);--popover-active-bg: var(--primary-01);--popover-focus-bg: var(--neutral-00);display:flex;flex:1;flex-direction:row;align-items:center;padding:var(--popover-menu-item-padding);min-width:var(--popover-menu-min-width);height:var(--popover-menu-height);border:none;background-color:transparent;color:var(--popover-text-color);font-size:var(--popover-text-font-size);font-weight:500;letter-spacing:-.017em;line-height:1.125rem;cursor:pointer;-webkit-user-select:none;user-select:none;gap:var(--popover-icon-margin-right)}:host:hover{background-color:var(--popover-hover-bg)}:host:active{background-color:var(--popover-active-bg)}:host:focus-visible{background-color:var(--popover-focus-bg)}:host .menu-item-icon{display:flex;align-items:center;justify-content:center;width:1rem;height:1rem}:host .menu-item-icon img{width:100%;height:100%}\n"] }]
        }], ctorParameters: function () { return [{ type: MenuComponent, decorators: [{
                    type: Optional
                }] }, { type: undefined, decorators: [{
                    type: Attribute,
                    args: ['class']
                }] }]; }, propDecorators: { label: [{
                type: Input
            }], icon: [{
                type: Input
            }], iconClass: [{
                type: Input,
                args: ['icon-class']
            }], forMenu: [{
                type: Input
            }], onMouseEnter: [{
                type: HostListener,
                args: ['mouseenter', ['$event']]
            }], onClick: [{
                type: HostListener,
                args: ['click', ['$event']]
            }] } });

/**
 * Import the MenuModule into the NgModule in which you want to create menus.
 * You can then apply menu directives to build your custom menu. A typical menu consists of the following directives:
 *
 * forContextMenu - opens a context menu when the user clicks on the menu item
 * forMenu - opens a menu when the user clicks on the menu item
 */
class MenuDirective {
    constructor(menuService, parent, self) {
        this.menuService = menuService;
        this.parent = parent;
        this.self = self;
        this.forContextMenu = '';
        this.forMenu = '';
    }
    onContextMenuClick(event) {
        event.stopPropagation();
        event.preventDefault();
        this.menuService.menuItems.forEach((item) => {
            item.component.close();
            if (this.forContextMenu === item.id) {
                item.component.listener$.emit({ state: true, event: event, position: 'mouse' });
            }
        });
    }
    onMouseEnter(event) {
        this.closeChildren(this.parent);
        if (this.forMenu && this.parent) {
            const item = this.menuService.findComponent(this.forMenu);
            if (item) {
                this.parent && (this.parent.child = item.component);
                !item?.component.showing && item?.component.listener$.emit({ state: true, event, position: 'right' });
                this.menuService.setClickedMenuState(true, item.component);
            }
        }
    }
    onMouseClick(event) {
        event.stopPropagation();
        event.preventDefault();
        this.closeChildren(this.parent);
        if (this.forMenu && !this.parent) {
            this.closeAll();
            const item = this.menuService.findComponent(this.forMenu);
            if (item) {
                // this.parent && (this.parent.child = item.component);
                !item?.component.showing && item?.component.listener$.emit({ state: true, event: event, position: 'bottom' });
            }
        }
    }
    /**
     * If the component has a child, and the child's id is not the same as the id of the menu that was
     * clicked, then emit a state of false to the child's listener
     * @param {MenuComponent} component - MenuComponent - the component that is being passed in
     */
    closeChildren(component) {
        if (component && component.child) {
            if (component.child.id !== this.forMenu) {
                component.child.listener$.emit({ state: false, event: event, position: 'bottom' });
            }
            this.closeChildren(component.child);
        }
    }
    /**
     * It loops through all the menu items and calls the close() function on each one
     */
    closeAll() {
        this.menuService.menuItems.forEach((item) => {
            item.component.close();
        });
    }
}
MenuDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.7", ngImport: i0, type: MenuDirective, deps: [{ token: MenuService }, { token: MenuComponent, optional: true }, { token: MenuItemComponent, optional: true, self: true }], target: i0.ɵɵFactoryTarget.Directive });
MenuDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "15.2.7", type: MenuDirective, selector: "[forContextMenu],[forMenu]", inputs: { forContextMenu: "forContextMenu", forMenu: "forMenu" }, host: { listeners: { "contextmenu": "onContextMenuClick($event)", "mouseenter": "onMouseEnter($event)", "click": "onMouseClick($event)" } }, ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.7", ngImport: i0, type: MenuDirective, decorators: [{
            type: Directive,
            args: [{
                    // eslint-disable-next-line @angular-eslint/directive-selector
                    selector: '[forContextMenu],[forMenu]',
                }]
        }], ctorParameters: function () { return [{ type: MenuService }, { type: MenuComponent, decorators: [{
                    type: Optional
                }] }, { type: MenuItemComponent, decorators: [{
                    type: Optional
                }, {
                    type: Self
                }] }]; }, propDecorators: { forContextMenu: [{
                type: Input
            }], forMenu: [{
                type: Input
            }], onContextMenuClick: [{
                type: HostListener,
                args: ['contextmenu', ['$event']]
            }], onMouseEnter: [{
                type: HostListener,
                args: ['mouseenter', ['$event']]
            }], onMouseClick: [{
                type: HostListener,
                args: ['click', ['$event']]
            }] } });

/**
 * The menu module provides directives to help create custom menu interactions.
 *
 * ### Installation
 *
 * All public npm packages of TUBITAK is at [https://www.npmjs.com/~tbtk](https://www.npmjs.com/~tbtk).
 * To install MenuModule:
 *
 * ```bash
 * $ npm set registry https://registry.npmjs.org/
 * $ npm install @tbtk/menu -s
 * ```
 *
 * Just import it to your project of `@NgModule` import section.
 *
 * ```typescript
 * @NgModule({
 *   declarations: [AppComponent, MenuDirective],
 *   imports: [CommonModule, MenuModule],
 * })
 * export class AppModule {
 * }
 * ```
 */
class MenuModule {
}
MenuModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.7", ngImport: i0, type: MenuModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
MenuModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "15.2.7", ngImport: i0, type: MenuModule, declarations: [MenuComponent, MenuItemComponent, MenuDirective], imports: [CommonModule, PortalModule, OverlayModule], exports: [MenuComponent, MenuItemComponent, MenuDirective] });
MenuModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "15.2.7", ngImport: i0, type: MenuModule, imports: [CommonModule, PortalModule, OverlayModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.7", ngImport: i0, type: MenuModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [MenuComponent, MenuItemComponent, MenuDirective],
                    imports: [CommonModule, PortalModule, OverlayModule],
                    exports: [MenuComponent, MenuItemComponent, MenuDirective],
                }]
        }] });

/*
 * Public API Surface of menu
 */

/**
 * Generated bundle index. Do not edit.
 */

export { MenuComponent, MenuDirective, MenuItemComponent, MenuModule, MenuService };
